import {
  Box, DashboardModule, DashboardModuleListItem, Grid, Image,
} from '@domain-group/suit-theme'

import useAgency from '@hooks/useAgency'
import useAttachment from '@hooks/useAttachment'
import useBrand from '@hooks/useBrand'
import useSelector from '@hooks/useSelector'
import useTeamMembers from '@hooks/useTeamMembers'

import getFullAddress from '@functions/address'
import combineAgentsAndTeamMembers from '@functions/campaign'

import BrandedModuleHeader from '@sections/Public/packs/Campaign/components/BrandedModuleHeader'

import AgentListItem from './AgentListItem'

interface AgencyDetailsProps {
  agencyId: number,
  agentIds: number[],
  brandId: number,
  logoId: number,
  teamId?: number,
}

const AgencyAndAgentsDetails = (props: AgencyDetailsProps) => {
  const {
    agencyId, agentIds, brandId, logoId, teamId,
  } = props

  const agencyPayload = useAgency({ id: agencyId })
  const {
    agency,
    agency: {
      address_line_1,
      address_line_2,
      address_region,
    },
    loading,
  } = agencyPayload

  const brandPayload = useBrand({ id: brandId })
  const { publicLinkTextAccentColour } = brandPayload

  const logoAttachmentPayload = useAttachment({ id: logoId })
  const { attachment: brandLogoAttachment } = logoAttachmentPayload

  const logoUrl = brandLogoAttachment?.links?.original_url || ''

  const agents = useSelector(reduxState => reduxState.entities.agents)

  const { filteredTeamMembers } = useTeamMembers({ filters: { team_id: teamId } })
  const teamMembers = teamId ? filteredTeamMembers : []

  const filteredMembersAndAgents = combineAgentsAndTeamMembers(teamMembers, agentIds, agents)
  const hasFilteredMembersAndAgents = !!filteredMembersAndAgents.length

  const agencyAddress = getFullAddress({
    address_line_1,
    address_line_2,
    address_region,
  }, { multiLine: false, localAddress: true })

  return (
    <DashboardModule
      boxProps={{ backgroundColor: 'backgroundColor', paddingTop: 'large' }}
      contentBoxProps={{ flexDirection: 'column', overflow: 'hidden' }}
      customHeader={(
        <BrandedModuleHeader
          accentColor={publicLinkTextAccentColour}
          backgroundColour="backgroundColor"
          title="Agency Details"
          boxProps={{ paddingX: 'large' }}
        />
      )}
      loading={loading}
    >
      <DashboardModuleListItem
        boxProps={{
          backgroundColor: 'backgroundColor', borderBottom: 'none', paddingY: 'large', paddingX: 'large',
        }}
        // @ts-expect-error TS2322
        imageComponent={logoUrl.length && (
          <Image
            backgroundSize="contain"
            borderRadius={5}
            style={{ border: '1px solid #ececec', height: '64px', width: '101px' }}
            url={logoUrl}
          />
        )}
        secondaryText={agencyAddress}
        title={agency.title}
      />

      <Box paddingX="medium" paddingTop="large" backgroundColor="#ffffff">
        <BrandedModuleHeader
          accentColor={publicLinkTextAccentColour}
          boxProps={{
            paddingX: 'medium',
          }}
          title="Agent Details"
        />
      </Box>

      <Box backgroundColor="white">
        {hasFilteredMembersAndAgents && (
          <Grid
            // @ts-expect-error TS2322
            gridTemplateColumns="repeat(4, 1fr)"
            paddingBottom="large"
            paddingTop="medium"
          >
            {filteredMembersAndAgents.map((member, index) => {
              const userImage = Object.entries(agents).filter(
                agent => agent[1].user_id === member.user_id,
              // @ts-expect-error TS2339
              )[0][1].default_image
              const hideLeftBorder = (index === 0) || (index % 4 === 0)

              return (
                <AgentListItem
                  key={member.user_id}
                  member={member}
                  userImage={userImage}
                  hideLeftBorder={hideLeftBorder}
                />
              )
            })}
          </Grid>
        )}
      </Box>
    </DashboardModule>
  )
}

export default AgencyAndAgentsDetails
